import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useIdentity from "../../hooks/useIdentity";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import useUserApi from "../../http/user/api";
import { UpsertUser, User } from "../../http/user/type";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import UserForm, { UserFormBody } from "./UserForm";

const Users: FC = () => {
  const usersApi = useUserApi();
  const i18n = useI18n();
  const { open, close, isOpen } = useToggle();
  const dispatch = useContext(NotificationDispatch);
  const pageContext = usePageContext();
  const aborter = useRef(new AbortController());
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const [editUser, setEditUser] = useState<User>();
  const identity = useIdentity();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = usersApi.useUsers(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.users.plural });
  }, [i18n.language]);

  const onSubmit = async (formData: UserFormBody) => {
    const body: UpsertUser = {
      id: formData.user?.id!,
      roles: formData.user?.roles ?? [],
      permissions: formData.user?.permissions ?? [],
    };
    if (!editUser) {
      try {
        await usersApi.createUser(body);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      try {
        await usersApi.updateUser(body);
        setEditUser(undefined);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<User>[] = [
    {
      title: i18n.t.common.edit,
      onClick: setEditUser,
      icon: SvgEdit,
    },
  ];

  return (
    <>
      <Sidebar isOpen={isOpen} onClose={close} title={i18n.t.users.addUser}>
        <UserForm key={isOpen ? (editUser?.id ?? "new") : "closed"} onSubmit={onSubmit}></UserForm>
      </Sidebar>
      <Table<User>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <tr>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.email}</th>
            <th>{i18n.t.common.roles}</th>
            <th>{i18n.t.common.permissions}</th>
            <th></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>
              {item.firstName} {item.lastName}
            </td>
            <td>{item.email}</td>
            <td align="center">
              {item.roles.map((role) => (
                <div key={role.name}>{role.name}</div>
              ))}
            </td>
            <td align="center">
              {item.permissions.map((permission) => (
                <div key={permission.name}>{permission.name}</div>
              ))}
            </td>
          </>
        )}
        onRowClick={(user) => setEditUser(user)}
        filters={filters}
        items={data?.items}
        total={data?.total}
        error={error}
        rowActions={actions}
      ></Table>
      <Sidebar isOpen={!!editUser} title={i18n.t.users.editUser} onClose={() => setEditUser(undefined)}>
        <UserForm
          isDisabled={!editUser?.email || identity.email === editUser?.email}
          key={isOpen ? (editUser?.id ?? "new") : "closed"}
          onSubmit={onSubmit}
          user={editUser}
        ></UserForm>
      </Sidebar>
    </>
  );
};

export default Users;
